import axios, { AxiosResponse } from "axios";
import Cookies from "universal-cookie";
import config from "../../config";

export interface GetQuestDataParams {
  userId: string;
  token: string;
  entityId: string;
  apiKey: string;
  campaignId: string;
  variation?: string;
  BACKEND_URL: string;
}

export interface QuestDataResponse {
  success: boolean;
  data: any;
  message?: string;
}

export const getQuestData = async ({
  userId,
  token,
  entityId,
  apiKey,
  campaignId,
  variation = "",
  BACKEND_URL,
}: GetQuestDataParams): Promise<QuestDataResponse | null> => {
  const headers = {
    apiKey,
    userId,
    token,
  };

  const params = new URLSearchParams({
    platform: "REACT",
    variation,
  });

  const url = `${BACKEND_URL}api/v2/entities/${entityId}/campaigns/${campaignId}?${params.toString()}`;

  try {
    const response: AxiosResponse<QuestDataResponse> = await axios.get(url, {
      headers,
    });
    if (response.data.success) {
      return response.data;
    } else {
      console.error(
        "API returned an error:",
        response.data.message || "Unknown error"
      );
      return null;
    }
  } catch (error) {
    console.error("Error fetching quest data:", error);
    return null;
  }
};

interface GetExternalLoginParams {
  entityId: string;
  apiKey: string;
  userId?: string;
  uniqueUserId?: string;
  uniqueEmailId?: string;
  token?: string;
  apiType?: string;
  entityAuthenticationToken?: string;
}

interface LoginResponse {
  token: string;
  userId: string;
}

export const getExternalLogin = async ({
  entityId,
  apiKey,
  uniqueUserId,
  uniqueEmailId,
  userId,
  token,
  apiType,
  entityAuthenticationToken,
}: GetExternalLoginParams): Promise<LoginResponse | null> => {
  const cookies = new Cookies();
  const externalUserId = cookies.get("externalUserId");
  const questUserId = cookies.get("questUserId");
  const questUserToken = cookies.get("questUserToken");
  let BACKEND_URL =
    apiType == "STAGING" ? config.BACKEND_URL_STAGING : config.BACKEND_URL;

  if (!entityId) {
    console.error("Entity ID is required.");
    return null;
  }

  const headers = {
    apiKey,
    ...(token && { token }),
    ...(userId && { userId }),
    ...(!!entityAuthenticationToken && { "entity-authentication-token": entityAuthenticationToken }),
  };

  const body = {
    externalUserId: uniqueUserId || null,
    entityId,
    email: uniqueEmailId || null,
    ignoreMAU: true,
  };

  if (
    !!externalUserId &&
    !!questUserId &&
    !!questUserToken &&
    (externalUserId == uniqueUserId || externalUserId == uniqueEmailId)
  ) {
    return { userId: questUserId, token: questUserToken };
  } else if (uniqueUserId) {
    try {
      const response = await axios.post(
        `${BACKEND_URL}api/users/external/login`,
        body,
        { headers }
      );
      const { userId, token } = response.data;

      const expires = new Date();
      expires.setHours(expires.getHours() + 12);

      cookies.set("externalUserId", uniqueUserId, { path: "/", expires });
      cookies.set("questUserId", userId, { path: "/", expires });
      cookies.set("questUserToken", token, { path: "/", expires });

      return { token, userId };
    } catch (error) {
      console.error("Error during external login:", error);
      return null;
    }
  }

  return null;
};

interface ClaimCampaignParams {
  userId: string;
  token: string;
  entityId: string;
  apiKey: string;
  campaignId: string;
  BACKEND_URL: string;
  data: any;
}
export const claimCampaign = async ({
  userId,
  token,
  entityId,
  apiKey,
  campaignId,
  BACKEND_URL,
  data
}: ClaimCampaignParams) => {
  const headers = {
    apiKey,
    userId,
    token,
  };

  const url = `${BACKEND_URL}api/v2/entities/${entityId}/campaigns/${campaignId}/verify`;

  try {
    const response: AxiosResponse<QuestDataResponse> = await axios.post(url, data, {
      headers,
    });
    if (response.data.success) {
      return response.data;
    } else {
      console.error(
        "API returned an error:",
        response.data.message || "Unknown error"
      );
      return null;
    }
  } catch (error) {
    console.error("Error claiming campaign:", error);
    return null;
  }
};